import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://www.grandframe.in';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;
const ORG_NAME = 'Grandframe Creative Digital';

const SEO = ({ 
  title = `Flex Printing & Digital Marketing Agency in Alappuzha | ${ORG_NAME}`, 
  description = "Best flex printing, banner printing, visiting card & brochure printing services in Alappuzha, Kerala. Walk-in studio at Kalarcode for same-day flex banners, digital marketing, photography & web development.", 
  keywords = "flex printing Alappuzha, flex banner printing near me, visiting card printing Alappuzha, brochure printing Kerala, banner printing Kalarcode, digital marketing agency Alappuzha, printing services near me",
  url = `${BASE_URL}/`,
  image = DEFAULT_IMAGE,
  type = "website",
  schema = null,           // accept fully custom JSON-LD object or array
  article = null,          // { datePublished, dateModified, author } for blog posts
}) => {

  // Base LocalBusiness schema injected on EVERY page
  const baseSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}/#organization`,
    "name": ORG_NAME,
    "alternateName": "Grandframe",
    "description": "Best flex printing, banner printing, visiting card printing & digital marketing agency in Alappuzha, Kerala. Walk-in studio at Kalarcode for all printing services.",
    "url": BASE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": `${BASE_URL}/logo.svg`,
      "width": 250,
      "height": 60
    },
    "image": DEFAULT_IMAGE,
    "telephone": "+919562001221",
    "email": "contact@grandframe.in",
    "priceRange": "₹₹",
    "currenciesAccepted": "INR",
    "paymentAccepted": "Cash, Bank Transfer, UPI",
    "foundingDate": "2021",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Kalarcode, Alappuzha",
      "addressRegion": "Kerala",
      "postalCode": "688003",
      "streetAddress": "Kaithavana, Alappuzha - Changanassery Highway",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 9.4981,
      "longitude": 76.3388
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "areaServed": [
      { "@type": "City", "name": "Alappuzha" },
      { "@type": "City", "name": "Kottayam" },
      { "@type": "City", "name": "Pathanamthitta" },
      { "@type": "State", "name": "Kerala" }
    ],
    "sameAs": [
      "https://www.instagram.com/grandframe.in",
      "https://www.linkedin.com/company/grandframe",
      "https://www.facebook.com/grandframe.in"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Printing & Digital Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Flex Printing & Print Design",
            "description": "Professional flex banner printing, visiting cards, brochures, flyers, and stationery printing in Alappuzha"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Digital Marketing & Local SEO",
            "description": "Performance marketing, local SEO, Google Ads, and social media management in Kerala"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Web & E-Commerce Development",
            "description": "Custom, high-performance websites and e-commerce platforms for local businesses"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Creative Branding & Design",
            "description": "Logo design, brand identity, visual storytelling, and professional photography"
          }
        }
      ]
    }
  };

  // Compose schemas array
  const schemas = [baseSchema];
  if (schema) {
    if (Array.isArray(schema)) schemas.push(...schema);
    else schemas.push(schema);
  }
  if (article) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": title,
      "description": description,
      "image": image,
      "url": url,
      "datePublished": article.datePublished || "2026-07-12",
      "dateModified": article.dateModified || article.datePublished || "2026-07-12",
      "author": {
        "@type": "Person",
        "name": article.author || "Boban Das",
        "url": `${BASE_URL}/about`,
        "jobTitle": "CEO & Founder",
        "worksFor": {
          "@type": "Organization",
          "name": ORG_NAME,
          "url": BASE_URL
        }
      },
      "publisher": {
        "@type": "Organization",
        "name": ORG_NAME,
        "logo": {
          "@type": "ImageObject",
          "url": `${BASE_URL}/logo.svg`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url
      }
    });
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={ORG_NAME} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={article ? 'article' : type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content={ORG_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title} />

      {/* Article-specific OG tags */}
      {article && <meta property="article:published_time" content={article.datePublished || "2026-07-12"} />}
      {article && <meta property="article:author" content={article.author || "Boban Das"} />}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schemas.length === 1 ? schemas[0] : { "@context": "https://schema.org", "@graph": schemas })}
      </script>
    </Helmet>
  );
};

export default SEO;
